import moment from "moment-timezone";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";
import { isAdmin } from "shared/utils/env";
import formatFileSize from "shared/utils/formatFileSize";
import { isLanguageChosen } from "shared/utils/languageChoice";

const namespaces = isAdmin ? ["admin"] : ["partner", "website", "whitelabel"];

export const setTimeZone = (timeZone) => {
  moment.tz.setDefault(timeZone);
};

export const getTimeZone = () => {
  return moment.tz.guess();
};

const { REACT_APP_TRANSLATION_VERSION } = process.env;

/**
 * Язык кабинета по умолчанию русский. Язык браузера не учитываем: берём
 * ?lng= из адреса, затем язык, который пользователь сам выбрал в настройках
 * (в localStorage детектор кэширует и угаданный язык, поэтому без отметки
 * о выборе его не читаем), иначе русский. Админку не трогаем: там прежний
 * порядок с языком браузера.
 */
export const DEFAULT_PARTNER_LANGUAGE = "ru";

const languageDetector = new LanguageDetector();

languageDetector.addDetector({
  name: "chosenLocalStorage",
  lookup() {
    if (!isLanguageChosen()) {
      return undefined;
    }

    try {
      return localStorage.getItem("i18nextLng") || undefined;
    } catch (error) {
      return undefined;
    }
  },
});

languageDetector.addDetector({
  name: "partnerDefault",
  lookup() {
    return DEFAULT_PARTNER_LANGUAGE;
  },
});

const detection = isAdmin
  ? undefined
  : {
      order: ["querystring", "chosenLocalStorage", "partnerDefault"],
      caches: ["localStorage"],
    };

i18n
  .use(HttpBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: "en",
    detection,
    supportedLngs: ["en", "ru"],
    debug: false,
    ns: namespaces,
    defaultNS: namespaces[0],
    interpolation: {
      escapeValue: false, // react already safes from xss
      format(value, format, lng) {
        const momentValue = moment(value).locale(lng);

        if (format === "dateMonthFull") {
          if (lng === "en") {
            return momentValue.format("MMMM[,] D YYYY");
          }
          if (lng === "de") {
            return momentValue.format("D[.] MMMM YYYY");
          }
          return momentValue.format("D MMMM YYYY");
        }

        if (format === "local") {
          return momentValue.format("LLL");
        }

        if (format === "dateTime") {
          return momentValue.format("DD.MM.YYYY HH:mm");
        }

        if (format === "short") {
          return momentValue.format("DD MMMM YYYY");
        }

        if (format === "day") {
          return momentValue.format("DD");
        }

        if (format === "month") {
          return momentValue.format("MMMM YYYY");
        }

        if (format === "time") {
          return momentValue.format("HH:mm");
        }

        if (format === "dateTimeSeconds") {
          return momentValue.format("DD.MM.YYYY HH:mm:ss");
        }

        if (format === "timeSeconds") {
          return momentValue.format("HH:mm:ss");
        }

        if (format === "full") {
          return momentValue.format("DD MMMM YYYY HH:mm");
        }

        if (format === "fromNow") {
          return momentValue.fromNow(true);
        }

        if (format === "fromNowAgo") {
          return momentValue.fromNow();
        }

        if (format === "humanize") {
          return moment.duration(value, "seconds").humanize(true);
        }

        if (format === "filesize") {
          return formatFileSize(value);
        }

        return value;
      },
    },
    saveMissing: true,
    updateMissing: true,
    appendNamespaceToMissingKey: true,
    backend: {
      loadPath: `/api/v1/translations/partner/${REACT_APP_TRANSLATION_VERSION}/{{ns}}/{{lng}}`,
      addPath: `/api/v1/translations/partner/${REACT_APP_TRANSLATION_VERSION}/{{ns}}/{{lng}}`,
    },
  });

export default i18n;
